import {
  Alert,
  AlertDescription,
  AlertIcon,
  Flex,
  Grid,
  GridItem,
  Heading,
  Skeleton,
  Spacer,
  Stack,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { LexicalEditor } from "lexical";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { FormikSwitchInput } from "src/components/Inputs/FormikSwitchInput";
import { FormMustacheInput } from "src/components/Inputs/FormMustacheInput";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { Glossary } from "src/components/Text/Glossary";
import { EditorRef } from "src/components/WysiwygEditor/lexical/Editor";
import FormikLexicalEditor from "src/components/WysiwygEditor/lexical/FormikEditor";
import {
  editorStateToHtml,
  editorStateToPlainText,
  importHtml,
  importPlainText,
} from "src/components/WysiwygEditor/lexical/utils/converters";
import { $validateEditorState } from "src/components/WysiwygEditor/lexical/utils/validator";
import {
  LOTTERY_AND_OFFERS_DISABLED_MESSAGE,
  MESSAGE_CONTENT_LIMIT,
  MESSAGE_TEMPLATE_CATEGORIES,
  MESSAGE_TEMPLATES,
  MessageTemplateCategory,
} from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useMessageRichContent } from "src/hooks/useMessageRichContent";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataMutation } from "src/hooks/useRemoteDataMutation";
import { toSegmentedMessage } from "src/hooks/useSegmentedMessage";
import {
  hasChanges,
  MessageTemplateId,
} from "src/scenes/orgAdmin/formTemplates/types/common";
import { generateAutomaticMessageSchema } from "src/schemas/Message";
import { validateWithZod } from "src/services/formValidations";
import { mustacheSafeParse } from "src/services/mustache";
import * as Url from "src/services/url";
import {
  FORM_TEMPLATE_TAB_HASH,
  FormTemplates,
} from "src/services/url/OrgAdmin";
import { FormikErrorAccumulator } from "src/types/FormikErrorsAccumulator";
import * as GQL from "src/types/graphql";
import { VariablesContext } from "src/types/messages/variables";
import { z } from "zod";
import { UPDATE_MESSAGE_TEMPLATE } from "../../graphql/mutations";
import {
  GET_MESSAGE_TEMPLATE,
  GET_MESSAGE_TEMPLATE_TYPES,
} from "../../graphql/queries";
import { PreviewMessageTemplate } from "./components/PreviewMessageTemplate";
import { useEditFormTemplateEditMessagesContext } from "./context";
import { EditMessageTemplateActionBar } from "./EditMessageTemplateActionBar";

const FORM_ID = "edit-form-templates-edit-message-template";

interface Props {
  isLoading: boolean;
  existingTemplateId?: string;
  existingContentId?: string;
  schoolName?: string;
  schoolSpecificEnabled: boolean;
  templateDisabled?: boolean;
}

export const EditMessageTemplateForm: FunctionComponent<Props> = ({
  isLoading,
  existingTemplateId,
  existingContentId,
  schoolName,
  schoolSpecificEnabled,
  templateDisabled,
}) => {
  const organization = useOrganization();
  const {
    formTemplateId = "",
    messageTemplateType = "",
    enrollmentPeriodId = "",
    schoolId: schoolIdParam = "",
    isViewOnly = "",
  } = Url.OrgAdmin.MessageTemplate.useParams();
  const [isPreview, setIsPreview] = useState(isViewOnly === "true");
  const { state, dispatch } = useEditFormTemplateEditMessagesContext();

  const templateType = messageTemplateType as GQL.message_template_type_enum;
  const offerRelated =
    MESSAGE_TEMPLATE_CATEGORIES.find((category) =>
      category.messageTemplateTypes.includes(templateType)
    )?.key === MessageTemplateCategory.LotteryAndOffers;
  const schoolId =
    schoolSpecificEnabled && schoolIdParam !== "standard"
      ? schoolIdParam
      : null;

  const [updateMessageTemplate] = useRemoteDataMutation<
    GQL.UpdateMessageTemplate,
    GQL.UpdateMessageTemplateVariables
  >(UPDATE_MESSAGE_TEMPLATE);

  const toast = useAvelaToast();
  const navigate = useNavigate();

  const [emailEditor, setEmailEditor] = useState<LexicalEditor>();
  const emailEditorRef: EditorRef = (editor: LexicalEditor) => {
    if (editor != null) setEmailEditor(editor);
  };

  const [smsEditor, setSmsEditor] = useState<LexicalEditor>();
  const smsEditorRef: EditorRef = (editor: LexicalEditor) => {
    if (editor != null) setSmsEditor(editor);
  };

  const featureFlag = useFlags(["segmented_message_warning"]);

  const { uploadRichContent, deleteRichContent } = useMessageRichContent(
    emailEditor,
    smsEditor
  );

  const getEmailMarkup = useCallback(() => {
    if (emailEditor) {
      return editorStateToHtml(emailEditor);
    }
  }, [emailEditor]);

  const getEmailText = useCallback(() => {
    if (emailEditor) {
      return editorStateToPlainText(emailEditor);
    }
  }, [emailEditor]);

  const getEmailLexical = useCallback(() => {
    if (emailEditor) {
      return JSON.stringify(emailEditor.getEditorState().toJSON());
    }
  }, [emailEditor]);

  const getSmsBody = useCallback(() => {
    if (smsEditor) {
      return editorStateToPlainText(smsEditor);
    }
  }, [smsEditor]);

  const getSmsLexical = useCallback(() => {
    if (smsEditor) {
      return JSON.stringify(smsEditor.getEditorState().toJSON());
    }
  }, [smsEditor]);

  const variablesContext = useMemo(
    () =>
      ({
        type: "triggered",
        messageTemplateType: templateType,
      } satisfies VariablesContext),
    [templateType]
  );

  const AutomaticMessageSchema = useMemo(
    () => generateAutomaticMessageSchema(),
    []
  );

  type AutomaticMessageFormType = z.infer<typeof AutomaticMessageSchema>;

  const saveChanges = useCallback(
    async (values: AutomaticMessageFormType) => {
      try {
        const { document_id, messageDocument } = await uploadRichContent({
          subject: values.emailSubject,
          language: "en",
        });
        const templateData = {
          form_template_id: formTemplateId,
          type: templateType,
          school_id: schoolId,
          email_subject: values.emailSubject,
          email_plain_text: values.emailText,
          email_markup: messageDocument?.html,
          sms_body: values.smsBody,
          enabled: values.enabled,
        };

        await updateMessageTemplate({
          variables: {
            insert_object: {
              ...templateData,
              message_template_rich_contents: {
                data: [
                  {
                    content_id: document_id,
                    language: "en",
                  },
                ],
              },
            },
            update_object: templateData,
            existing_template_id: existingTemplateId ?? "",
            is_existing:
              existingTemplateId !== undefined &&
              existingTemplateId !== null &&
              existingTemplateId !== "",
            new_content_id: document_id ?? "",
            existing_content_id: existingContentId ?? "",
            skip_delete: !existingContentId,
          },
          refetchQueries: [
            GET_MESSAGE_TEMPLATE,
            {
              query: GET_MESSAGE_TEMPLATE_TYPES,
              variables: {
                formTemplateId: formTemplateId,
              },
            },
          ],
        });

        if (existingContentId) {
          await deleteRichContent(existingContentId);
        }

        if (dispatch) {
          dispatch({
            type: "Refresh",
            initialValues: { editMessages: values },
            messageTemplateType: templateType,
          });
        }

        toast({
          title: "Automatic message updated",
          status: "info",
        });

        const schoolIdParam = schoolId ? `?schoolId=${schoolId}` : "";

        // doing this after 250ms to allow dispatcher to work to Refresh the state
        // TODO: this can be solved with adding a new dispatch type "Submit" and "SubmitComplete"
        setTimeout(
          () =>
            navigate(
              organization
                .map(
                  (org) =>
                    `${FormTemplates.edit(
                      org,
                      enrollmentPeriodId,
                      formTemplateId
                    )}${schoolIdParam}#${
                      FORM_TEMPLATE_TAB_HASH.AUTOMATIC_MESSAGES
                    }`
                )
                .withDefault("#")
            ),
          250
        );
      } catch (err: unknown) {
        console.error(err);
        toast.genericError();
      }
    },
    [
      uploadRichContent,
      formTemplateId,
      templateType,
      schoolId,
      updateMessageTemplate,
      existingTemplateId,
      existingContentId,
      dispatch,
      toast,
      deleteRichContent,
      navigate,
      organization,
      enrollmentPeriodId,
    ]
  );

  const draft = useMemo(
    () =>
      state?.get("editMessages")?.get(MessageTemplateId(templateType))?.draft,
    [templateType, state]
  );

  const original = useMemo(
    () =>
      state?.get("editMessages")?.get(MessageTemplateId(templateType))
        ?.original,
    [templateType, state]
  );

  useEffect(() => {
    // do nothing if not resetting
    if (!state?.get("reset")) return;

    // if editor or original not found, do nothing
    if (!emailEditor || !smsEditor || !original) return;

    // if original has lexical state json, parse it and set the editor state
    if (original.emailLexical) {
      emailEditor.update(() => {
        if (!original.emailLexical) return;

        const parsedEditorState = emailEditor.parseEditorState(
          original.emailLexical
        );
        emailEditor.setEditorState(parsedEditorState);
      });
    }
    // if original has legacy markup, import it
    else if (original.legacyMarkup) {
      importHtml(emailEditor, original.legacyMarkup);
    }

    // if original has sms lexical state json, parse it and set the editor state
    if (original.smsLexical) {
      smsEditor.update(() => {
        if (!original.smsLexical) return;

        const parsedEditorState = smsEditor.parseEditorState(
          original.smsLexical
        );
        smsEditor.setEditorState(parsedEditorState);
      });
    }
    // if original has sms body, import it
    else if (original.smsBody) {
      importPlainText(smsEditor, original.smsBody);
    }

    // changes are done, dispatch reset complete
    if (dispatch) {
      dispatch({
        type: "ResetComplete",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, emailEditor, original, state?.get("reset"), smsEditor]);

  useEffect(() => {
    // if editor or original not found, do nothing
    if (!emailEditor || !smsEditor || !original) return;

    // if original has legacy markup, import it
    if (!original.emailLexical && original.legacyMarkup) {
      importHtml(emailEditor, original.legacyMarkup);
    }

    // if original has sms body, import it
    if (!original.smsLexical && original.smsBody) {
      importPlainText(smsEditor, original.smsBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smsEditor, original]);

  const formikInitialValues = useMemo(() => {
    if (draft) {
      return draft;
    } else {
      return original;
    }
  }, [draft, original]);

  const hasChangesResult = useMemo(
    () => hasChanges(original, draft),
    [original, draft]
  );

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Exit without saving?",
    body: "There are unsaved changes on this form. If you exit now, you will lose your changes.",
    confirmButton: { label: "Exit without saving" },
  });

  const validateMessage = useCallback(
    async (values: AutomaticMessageFormType) => {
      const formikError =
        new FormikErrorAccumulator<AutomaticMessageFormType>();

      if (emailEditor && !$validateEditorState(emailEditor)) {
        formikError.add({
          emailLexical:
            "One or more variables in the email message are invalid. Correct or remove them and try again.",
        });
      } else if (values.emailMarkup) {
        formikError.add({
          emailLexical: mustacheSafeParse(
            values.emailMarkup,
            "One or more variables in the email message are invalid. Correct or remove them and try again."
          )[0],
        });
      }

      formikError.add({
        emailSubject: mustacheSafeParse(
          values.emailSubject,
          "One or more variables in the email subject line are invalid. Correct or remove them and try again."
        )[0],
      });

      formikError.add({
        emailText: mustacheSafeParse(
          values.emailText,
          "One or more variables in the email plain text message are invalid. Correct or remove them and try again."
        )[0],
      });

      if (smsEditor && !$validateEditorState(smsEditor)) {
        formikError.add({
          smsLexical:
            "One or more variables in the SMS message are invalid. Correct or remove them and try again.",
        });
      } else if (values.smsLexical) {
        formikError.add({
          smsLexical: mustacheSafeParse(
            values.smsBody,
            "One or more variables in the SMS message are invalid. Correct or remove them and try again."
          )[0],
        });
      }

      const segmentedMessage = toSegmentedMessage(values.smsBody, featureFlag);
      if (featureFlag["segmented_message_warning"].enabled) {
        if (segmentedMessage.isOverflowing()) {
          formikError.add({
            smsLexical: `SMS message exceeds character limit.`,
          });
        }
      } else {
        if (segmentedMessage.numberOfCharacters > MESSAGE_CONTENT_LIMIT) {
          formikError.add({
            smsLexical: `SMS message must be less than ${MESSAGE_CONTENT_LIMIT} characters.`,
          });
        }
      }

      const zodErrors = await validateWithZod(AutomaticMessageSchema)(values);
      if (zodErrors) {
        formikError.add(zodErrors);
      }
      return formikError.errors;
    },
    [emailEditor, smsEditor, featureFlag, AutomaticMessageSchema]
  );

  const handleValidate = useCallback(
    async (values: AutomaticMessageFormType) => {
      const updatedValues = {
        ...values,
        emailMarkup: getEmailMarkup() ?? "",
        emailText: getEmailText() ?? "",
        emailLexical: getEmailLexical(),
        smsLexical: getSmsLexical(),
        smsBody: getSmsBody() ?? "",
        isDraft: true as const,
      };

      if (dispatch && hasChanges(original, updatedValues)) {
        dispatch({
          type: "UpdateEditMessages",
          editMessages: updatedValues,
          messageTemplateType: templateType,
        });
      }

      return await validateMessage(updatedValues);
    },
    [
      dispatch,
      getEmailLexical,
      getEmailMarkup,
      getEmailText,
      getSmsBody,
      getSmsLexical,
      original,
      templateType,
      validateMessage,
    ]
  );

  if (!formikInitialValues) return <GenericError />;

  if (!formTemplateId || !templateType) {
    return <GenericError />;
  }

  return (
    <Formik<AutomaticMessageFormType>
      initialValues={formikInitialValues}
      onSubmit={saveChanges}
      enableReinitialize
      validateOnChange={false}
      validateOnBlur={false}
      validate={handleValidate}
    >
      {({ values, isSubmitting, dirty }) => {
        const handleResetForm = () => {
          // Reset the message template state
          if (dispatch) {
            dispatch({
              type: "Reset",
              messageTemplateType: templateType,
            });
          }
        };

        const toggle = (
          <FormikSwitchInput
            label={values.enabled ? "Active" : "Inactive"}
            name="enabled"
            checked={[true, ""]}
            unchecked={[false, ""]}
            direction="row"
            isDisabled={isPreview || templateDisabled}
          />
        );

        return (
          <Flex
            as={Form}
            id={FORM_ID}
            gap={4}
            minHeight="100%"
            direction="column"
            grow="1"
          >
            {confirmationDialog}
            <Heading as="h2" fontSize="xl" my={4} fontWeight={600}>
              {isPreview ? "Preview " : "Edit "}
              <Glossary>
                {`${MESSAGE_TEMPLATES[templateType].label} automatic message${
                  schoolSpecificEnabled && schoolId
                    ? ` for ${schoolName ?? schoolId}`
                    : ""
                }`}
              </Glossary>
            </Heading>
            {schoolSpecificEnabled && offerRelated && (
              <Alert status="info" borderRadius={6}>
                <AlertIcon />
                <AlertDescription fontSize="sm">
                  {schoolId
                    ? `You are viewing school specific content for ${
                        schoolName ?? schoolId
                      }.`
                    : `You are viewing the standard template which will be sent for all schools without school specific content enabled.`}
                </AlertDescription>
              </Alert>
            )}
            <Flex direction="column" gap={4}>
              <Heading as="h3" fontSize="lg" fontWeight={400}>
                Settings
              </Heading>

              {!isPreview && templateDisabled ? (
                <Tooltip
                  label={
                    <Text align="center">
                      {LOTTERY_AND_OFFERS_DISABLED_MESSAGE}
                    </Text>
                  }
                  hasArrow
                  placement="top"
                  borderRadius="md"
                  w={250}
                  p={2}
                >
                  <Flex alignSelf="flex-start">{toggle}</Flex>
                </Tooltip>
              ) : (
                toggle
              )}
              <Heading as="h3" fontSize="lg" fontWeight={400}>
                Content
              </Heading>
              <Heading as="h3" fontSize="md" fontWeight={400}>
                Email message
              </Heading>
              {isPreview ? (
                <PreviewMessageTemplate formValue={values} />
              ) : (
                <Stack gap="5" flexGrow="1">
                  {isLoading ? (
                    <Stack gap="5">
                      <Skeleton height="8" />
                      <Grid
                        templateColumns="repeat(auto-fit, minmax(30rem, 1fr))"
                        gap="5"
                        templateRows="auto"
                      >
                        <GridItem>
                          <Skeleton height="32" />
                        </GridItem>
                        <GridItem>
                          <Skeleton height="32" />
                        </GridItem>
                      </Grid>
                      <Skeleton height="8" />
                    </Stack>
                  ) : (
                    <>
                      <FormMustacheInput
                        id="emailSubject"
                        name="emailSubject"
                        label="Subject line"
                      />
                      <Grid
                        templateColumns="repeat(auto-fit, minmax(30rem, 1fr))"
                        gap="5"
                        templateRows="auto"
                        flexGrow="0"
                      >
                        <FormikLexicalEditor
                          name="emailLexical"
                          placeholder="Email content"
                          height="300px"
                          width="100%"
                          label="Content"
                          labelProps={{ fontSize: "sm", color: "gray.700" }}
                          isRequired={true}
                          editorRef={emailEditorRef}
                          settings={{
                            enableVariables: true,
                            variablesContext,
                          }}
                        />
                      </Grid>
                      <Flex direction="column" gap={4}>
                        <Heading as="h3" fontSize="md" fontWeight={400}>
                          SMS message
                        </Heading>
                        <FormikLexicalEditor
                          name="smsLexical"
                          placeholder="SMS content"
                          height="150px"
                          width="100%"
                          editorVersion="lite"
                          editorRef={smsEditorRef}
                          settings={{
                            enableVariables: true,
                            enableSmsStats: true,
                            variablesContext,
                          }}
                        />
                      </Flex>
                    </>
                  )}
                </Stack>
              )}
            </Flex>
            <Spacer />
            <EditMessageTemplateActionBar
              formId={FORM_ID}
              isPreview={isPreview}
              isViewOnly={isViewOnly === "true"}
              setIsPreview={setIsPreview}
              formTemplateId={formTemplateId}
              schoolId={schoolId}
              hasChanges={hasChangesResult || dirty}
              onClearChanges={handleResetForm}
              isSubmitting={isSubmitting}
              onConfirmBack={confirm}
            />
          </Flex>
        );
      }}
    </Formik>
  );
};

export const EditMessageTemplateSkeleton = () => (
  <Flex gap={4} minHeight="100%" direction="column" grow="1" p={4}>
    {/* Heading */}
    <Skeleton height="2rem" width="20rem" my={4} />

    {/* Settings section */}
    <Skeleton height="1.5rem" width="5rem" />
    <Skeleton height="1.5rem" width="8rem" />

    {/* Content section */}
    <Skeleton height="1.5rem" width="5rem" mt={4} />
    <Skeleton height="1.25rem" width="8rem" />

    {/* Email message */}
    <Stack gap={5} flexGrow={1}>
      {/* Subject line */}
      <Skeleton height="2.5rem" />

      {/* Email editor */}
      <Grid
        templateColumns="repeat(auto-fit, minmax(30rem, 1fr))"
        gap={5}
        templateRows="auto"
      >
        <GridItem>
          <Skeleton height="300px" />
        </GridItem>
      </Grid>

      {/* SMS section */}
      <Skeleton height="1.25rem" width="8rem" />
      <Skeleton height="150px" />
    </Stack>
  </Flex>
);
